import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const F = "'Poppins','Nunito',sans-serif";
const IST = () => new Date(new Date().getTime() + (330 - new Date().getTimezoneOffset()) * 60000);

function greeting() {
  const h = IST().getHours();
  return h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening';
}

const PLT = {
  facebook:  { label:'Facebook',      color:'#6C63FF', bg:'#EFEDFF' },
  instagram: { label:'Instagram',     color:'#E1306C', bg:'#FDEDF3' },
  whatsapp:  { label:'WhatsApp',      color:'#16A34A', bg:'#F0FDF4' },
  website:   { label:'Website Popup', color:'#D97706', bg:'#FFFBEB' },
};

/* ── Stat Card (matches sample coloured cards) ── */
function StatCard({ title, value, sub, gradient, icon }) {
  return (
    <div style={{
      background: gradient, borderRadius:18, padding:'22px 20px',
      boxShadow:'0 6px 24px rgba(0,0,0,0.10)', position:'relative',
      overflow:'hidden', minWidth:0,
    }}>
      <div style={{ fontSize:'0.6rem', fontFamily:F, fontWeight:700, color:'rgba(255,255,255,0.7)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:'1.8rem', fontFamily:F, fontWeight:800, color:'#fff', lineHeight:1, marginBottom:5 }}>{value}</div>
      <div style={{ fontSize:'0.68rem', fontFamily:F, color:'rgba(255,255,255,0.65)', fontWeight:500 }}>{sub}</div>
      <div style={{ position:'absolute', top:14, right:16, fontSize:'1.5rem', opacity:0.2 }}>{icon}</div>
      <div style={{ position:'absolute', bottom:-22, right:-22, width:88, height:88, borderRadius:'50%', background:'rgba(255,255,255,0.1)' }}/>
      <div style={{ position:'absolute', bottom:10, right:30, width:50, height:50, borderRadius:'50%', background:'rgba(255,255,255,0.07)' }}/>
    </div>
  );
}

/* ── Bar Chart ── */
function BarChart({ data }) {
  const max = Math.max(...data.map(d => d.total), 1);
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:110 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:5 }}>
          {d.total > 0 && (
            <span style={{ fontFamily:F, fontSize:'0.6rem', color:'#64748B', fontWeight:700 }}>{d.total}</span>
          )}
          <div style={{
            width:'100%', borderRadius:'6px 6px 0 0', minHeight:4,
            height:`${Math.max(Math.round((d.total / max) * 82), d.total ? 6 : 2)}px`,
            background: i % 2 === 0 ? 'linear-gradient(180deg,#A3E635,#84CC16)' : 'linear-gradient(180deg,#C4B5FD,#8B5CF6)',
            transition:'height 0.5s ease',
          }}/>
          <span style={{ fontFamily:F, fontSize:'0.62rem', color:'#94A3B8', fontWeight:500 }}>{d.day}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Table Row ── */
function TRow({ item, idx }) {
  const p = PLT[item.platform] || { label:item.platform, color:'#94A3B8', bg:'#F8FAFC' };
  const ok = item.status === 'success';
  const dt = new Date(item.created_at).toLocaleDateString('en-IN',{ day:'numeric', month:'short' });
  return (
    <tr style={{ background: idx % 2 === 0 ? '#fff' : '#FAFBFD' }}>
      <td style={{ padding:'12px 18px' }}>
        <span style={{ background:p.bg, color:p.color, borderRadius:8, padding:'4px 12px', fontFamily:F, fontWeight:700, fontSize:'0.7rem' }}>{p.label}</span>
      </td>
      <td style={{ padding:'12px 18px', fontFamily:F, fontSize:'0.76rem', color:'#475569' }}>{item.posted_date}</td>
      <td style={{ padding:'12px 18px', fontFamily:F, fontSize:'0.76rem', color:'#475569' }}>1 Post</td>
      <td style={{ padding:'12px 18px', fontFamily:F, fontSize:'0.76rem', color:'#475569' }}>{dt}</td>
      <td style={{ padding:'12px 18px' }}>
        <span style={{
          background: ok ? '#F0FDF4' : '#FEF2F2',
          color:      ok ? '#16A34A' : '#DC2626',
          borderRadius:20, padding:'4px 12px', fontFamily:F, fontSize:'0.65rem', fontWeight:700,
        }}>{ok ? '✓ Sent' : '✗ Failed'}</span>
      </td>
    </tr>
  );
}

/* ── Quick link row ── */
function QRow({ icon, label, sub, color, last }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 0', borderBottom: last ? 'none' : '1px solid #F1F5F9', cursor:'pointer' }}>
      <div style={{ width:38, height:38, borderRadius:10, background:`${color}1A`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', flexShrink:0 }}>{icon}</div>
      <div style={{ flex:1 }}>
        <div style={{ fontFamily:F, fontWeight:600, fontSize:'0.78rem', color:'#1E293B' }}>{label}</div>
        <div style={{ fontFamily:F, fontSize:'0.64rem', color:'#94A3B8', marginTop:1 }}>{sub}</div>
      </div>
      <span style={{ color:'#CBD5E1', fontSize:'0.9rem' }}>›</span>
    </div>
  );
}

/* ══ MAIN ══ */
export default function DashboardTab() {
  const [history,  setHistory]  = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [tests,    setTests]    = useState({ total:0, pending:0 });
  const [prayers,  setPrayers]  = useState({ total:0, unseen:0 });
  const [loading,  setLoading]  = useState(true);
  const [name,     setName]     = useState('Admin');

  useEffect(() => {
    const n = localStorage.getItem('zion_admin_name');
    if (n) setName(n);
    (async () => {
      const ago = new Date(IST().getTime() - 30*24*60*60*1000).toISOString().split('T')[0];
      const [h, s, t, p] = await Promise.all([
        supabase.from('post_history').select('*').gte('posted_date', ago).order('created_at',{ascending:false}),
        supabase.from('verse_schedule').select('scheduled_date,status,popup_active,image_url_en').order('scheduled_date',{ascending:true}),
        supabase.from('testimonials').select('id,status'),
        supabase.from('prayer_requests').select('id,seen'),
      ]);
      if (h.data) setHistory(h.data);
      if (s.data) setSchedule(s.data);
      if (t.data) setTests({ total:t.data.length, pending:t.data.filter(x=>x.status==='pending').length });
      if (p.data) setPrayers({ total:p.data.length, unseen:p.data.filter(x=>!x.seen).length });
      setLoading(false);
    })();
  }, []);

  const total    = history.length;
  const ok       = history.filter(h=>h.status==='success').length;
  const rate     = total > 0 ? Math.round((ok/total)*100) : 0;
  const recent   = history.slice(0,6);
  const upcoming = schedule.filter(s=>s.status==='scheduled').slice(0,3);
  const posted   = schedule.filter(s=>s.status==='posted').length;
  const byPlt    = ['facebook','instagram','whatsapp','website']
    .map(k => ({ k, v:history.filter(h=>h.platform===k).length, ...PLT[k] }))
    .filter(p => p.v > 0);

  const last7 = Array.from({length:7}).map((_,i) => {
    const d  = new Date(IST().getTime() - (6-i)*86400000);
    const ds = d.toISOString().split('T')[0];
    return { day:d.toLocaleDateString('en-IN',{weekday:'short'}), total:history.filter(h=>h.posted_date===ds).length };
  });

  const today = IST().toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'});

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:420, fontFamily:F }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:'3rem', marginBottom:14 }}>✝</div>
        <div style={{ color:'#84CC16', fontWeight:700, fontFamily:F }}>Loading dashboard…</div>
      </div>
    </div>
  );

  return (
    <div style={{ background:'#F8FAFC', minHeight:'100vh', fontFamily:F }}>

      {/* ── Top header bar ── */}
      <div style={{ background:'#fff', padding:'18px 28px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #F1F5F9', flexWrap:'wrap', gap:12, position:'sticky', top:0, zIndex:40 }}>
        <div>
          <h1 style={{ margin:0, fontFamily:F, fontWeight:800, fontSize:'1.45rem', color:'#1E293B' }}>Dashboard</h1>
          <div style={{ fontFamily:F, fontSize:'0.72rem', color:'#94A3B8', marginTop:3, fontWeight:500 }}>{today}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:38, height:38, borderRadius:10, background:'#F8FAFC', border:'1px solid #F1F5F9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', cursor:'pointer' }}>⚙</div>
          <div style={{ width:38, height:38, borderRadius:10, background:'#F8FAFC', border:'1px solid #F1F5F9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', cursor:'pointer' }}>🔔</div>
          <div style={{ display:'flex', alignItems:'center', gap:10, background:'#F8FAFC', border:'1px solid #F1F5F9', borderRadius:12, padding:'6px 14px 6px 8px' }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,#84CC16,#4D7C0F)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'0.9rem', fontWeight:800 }}>✝</div>
            <div>
              <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.8rem', color:'#1E293B' }}>{name}</div>
              <div style={{ fontFamily:F, fontSize:'0.62rem', color:'#94A3B8', fontWeight:500 }}>Church Admin</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body grid ── */}
      <div style={{ padding:'24px 28px', display:'grid', gridTemplateColumns:'1fr 272px', gap:22 }}>

        {/* LEFT */}
        <div style={{ minWidth:0 }}>

          {/* Stat cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:22 }}>
            <StatCard
              title="Total Posts (30d)" value={total}
              sub={`${rate}% success rate`}
              gradient="linear-gradient(135deg,#A78BFA,#7C3AED)" icon="📮"
            />
            <StatCard
              title="Success Rate" value={`${rate}%`}
              sub={`${ok} of ${total} delivered`}
              gradient="linear-gradient(135deg,#38BDF8,#0284C7)" icon="✅"
            />
            <StatCard
              title="Prayer Requests" value={prayers.total}
              sub={`${prayers.unseen} unseen`}
              gradient="linear-gradient(135deg,#86EFAC,#16A34A)" icon="🙏"
            />
          </div>

          {/* Chart + Analysis row */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 230px', gap:16, marginBottom:22 }}>

            {/* Bar chart card */}
            <div style={{ background:'#fff', borderRadius:16, padding:'20px 22px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
                <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.92rem', color:'#1E293B' }}>Weekly Post Activity</div>
                <button style={{ background:'#84CC16', border:'none', color:'#fff', fontFamily:F, fontWeight:700, fontSize:'0.68rem', padding:'6px 14px', borderRadius:8, cursor:'pointer', boxShadow:'0 3px 10px rgba(132,204,22,0.35)' }}>Export</button>
              </div>
              <BarChart data={last7}/>
            </div>

            {/* Analysis card */}
            <div style={{ background:'#fff', borderRadius:16, padding:'20px 18px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.92rem', color:'#1E293B', marginBottom:3 }}>More Analysis</div>
              <div style={{ fontFamily:F, fontSize:'0.68rem', color:'#94A3B8', marginBottom:16, fontWeight:500 }}>Platform breakdown</div>
              {byPlt.length === 0 ? (
                <div style={{ fontFamily:F, fontSize:'0.74rem', color:'#94A3B8', textAlign:'center', padding:'18px 0' }}>No post data yet</div>
              ) : byPlt.map((p, i) => (
                <div key={p.k} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 12px', background:'#F8FAFC', borderRadius:10, marginBottom:8, cursor:'pointer' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                    <div style={{ width:26, height:26, borderRadius:7, background:p.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <div style={{ width:9, height:9, borderRadius:'50%', background:p.color }}/>
                    </div>
                    <span style={{ fontFamily:F, fontWeight:600, fontSize:'0.74rem', color:'#1E293B' }}>{p.label.split(' ')[0]}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ fontFamily:F, fontWeight:700, fontSize:'0.74rem', color:'#1E293B' }}>{p.v}</span>
                    <span style={{ color:'#CBD5E1' }}>›</span>
                  </div>
                </div>
              ))}
              <div style={{ marginTop:12, display:'flex', alignItems:'center', gap:7 }}>
                <div style={{ width:20, height:20, borderRadius:'50%', background:'linear-gradient(135deg,#84CC16,#4D7C0F)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.6rem', color:'#fff' }}>✝</div>
                <span style={{ fontFamily:F, fontSize:'0.65rem', color:'#94A3B8', fontWeight:500 }}>Analysis by {name}</span>
              </div>
            </div>
          </div>

          {/* Recent Posts Table */}
          <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', overflow:'hidden' }}>
            <div style={{ padding:'18px 20px 14px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.92rem', color:'#1E293B' }}>Recent Posts</div>
              <button style={{ background:'#84CC16', border:'none', color:'#fff', fontFamily:F, fontWeight:700, fontSize:'0.68rem', padding:'6px 14px', borderRadius:8, cursor:'pointer' }}>Share</button>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'#F8FAFC' }}>
                    {['Platform','Date','Posts','Created','Status'].map(h => (
                      <th key={h} style={{ padding:'10px 18px', fontFamily:F, fontSize:'0.65rem', color:'#94A3B8', fontWeight:700, textAlign:'left', textTransform:'uppercase', letterSpacing:'0.08em', borderBottom:'1px solid #F1F5F9' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding:'36px', textAlign:'center', fontFamily:F, fontSize:'0.8rem', color:'#94A3B8' }}>No posts yet — start from Verse Post tab</td></tr>
                  ) : recent.map((item, i) => <TRow key={i} item={item} idx={i}/>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Dark overview card */}
          <div style={{ background:'linear-gradient(135deg,#1E3A5F 0%,#0F2040 100%)', borderRadius:20, padding:'22px 20px', position:'relative', overflow:'hidden', boxShadow:'0 8px 32px rgba(15,32,64,0.3)' }}>
            <div style={{ fontSize:'0.6rem', fontFamily:F, fontWeight:700, color:'rgba(255,255,255,0.5)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Church Overview</div>
            <div style={{ fontFamily:F, fontWeight:900, fontSize:'2.2rem', color:'#A3E635', lineHeight:1 }}>{tests.total}</div>
            <div style={{ fontFamily:F, fontSize:'0.68rem', color:'rgba(255,255,255,0.45)', marginTop:4, marginBottom:16 }}>Testimonials · {tests.pending} pending review</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
              {[
                { label:'Prayers',  value:prayers.total },
                { label:'Posted',   value:posted        },
                { label:'Sched.',   value:schedule.filter(s=>s.status==='scheduled').length },
                { label:'Unseen',   value:prayers.unseen },
              ].map((s,i) => (
                <div key={i} style={{ background:'rgba(255,255,255,0.07)', borderRadius:10, padding:'10px 12px' }}>
                  <div style={{ fontFamily:F, fontWeight:800, fontSize:'1.1rem', color:'#fff' }}>{s.value}</div>
                  <div style={{ fontFamily:F, fontSize:'0.6rem', color:'rgba(255,255,255,0.4)', marginTop:2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <button style={{ width:'100%', background:'#A3E635', border:'none', color:'#1E293B', fontFamily:F, fontWeight:800, fontSize:'0.74rem', padding:'11px', borderRadius:10, cursor:'pointer', boxShadow:'0 4px 14px rgba(163,230,53,0.4)' }}>
              View Details
            </button>
            <div style={{ position:'absolute', top:-22, right:-22, width:90, height:90, borderRadius:'50%', background:'rgba(163,230,53,0.08)' }}/>
            <div style={{ position:'absolute', bottom:-28, right:18, width:70, height:70, borderRadius:'50%', background:'rgba(163,230,53,0.05)' }}/>
          </div>

          {/* Upcoming card */}
          <div style={{ background:'#fff', borderRadius:16, padding:'18px 18px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.88rem', color:'#1E293B', marginBottom:14 }}>Upcoming Verses</div>
            {upcoming.length === 0 ? (
              <div style={{ fontFamily:F, fontSize:'0.74rem', color:'#94A3B8', textAlign:'center', padding:'16px 0' }}>No upcoming scheduled verses</div>
            ) : upcoming.map((s, i) => {
              const dt = new Date(s.scheduled_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',weekday:'short'});
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom: i < upcoming.length-1 ? '1px solid #F1F5F9' : 'none' }}>
                  <div style={{ width:38, height:38, borderRadius:9, background:'#F0FDF4', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
                    {s.image_url_en
                      ? <img src={s.image_url_en} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      : <span style={{ fontSize:'1.1rem' }}>📅</span>
                    }
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:F, fontWeight:600, fontSize:'0.76rem', color:'#1E293B' }}>{dt}</div>
                    <div style={{ fontFamily:F, fontSize:'0.63rem', color:'#94A3B8', marginTop:1 }}>Posts at 12:00 AM IST</div>
                  </div>
                  <span style={{ background:'#F0FDF4', color:'#16A34A', fontFamily:F, fontSize:'0.6rem', fontWeight:700, padding:'3px 9px', borderRadius:20, flexShrink:0 }}>Sched</span>
                </div>
              );
            })}
            <button style={{ width:'100%', background:'#84CC16', border:'none', color:'#fff', fontFamily:F, fontWeight:700, fontSize:'0.72rem', padding:'10px', borderRadius:10, marginTop:14, cursor:'pointer', boxShadow:'0 3px 10px rgba(132,204,22,0.35)' }}>
              Click for Full Schedule
            </button>
          </div>

          {/* Quick Access */}
          <div style={{ background:'#fff', borderRadius:16, padding:'18px 18px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ fontFamily:F, fontWeight:700, fontSize:'0.88rem', color:'#1E293B', marginBottom:10 }}>Quick Access</div>
            <QRow icon="✝"  label="Testimonials"   sub={`${tests.pending} pending review`}    color="#7C3AED" />
            <QRow icon="🙏" label="Prayer Requests" sub={`${prayers.unseen} unseen`}           color="#0284C7" />
            <QRow icon="📅" label="Verse Schedule"  sub={`${schedule.filter(s=>s.status==='scheduled').length} upcoming`} color="#16A34A" />
            <QRow icon="🖼️" label="Gallery"         sub="Manage church photos"                 color="#D97706" last/>
            <button style={{ width:'100%', background:'transparent', border:'2px dashed #E2E8F0', color:'#94A3B8', fontFamily:F, fontWeight:700, fontSize:'0.7rem', padding:'9px', borderRadius:10, marginTop:12, cursor:'pointer' }}>
              + Add more members
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
